import { submitFeedback } from '@/lib/recepthor-api';
import { Alert, Button, Radio, Textarea } from '@/ui';
import { DeleteIcon } from '@/ui/icons';
import { useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Shared bug-report / feedback form. Owns its own state, submit mutation and
// success view; container-agnostic. Rendered two ways:
//   - variant="panel"  → inside the floating FeedbackWidget FAB
//   - variant="modal"  → inside the Ayuda "Reportar un problema" modal
// The backend contract (POST /feedback → { data: { issueIdentifier, issueUrl } })
// lives in recepthor-api; this component never fakes a submit.

// ─── Constants ───────────────────────────────────────────────────────────────

const FEEDBACK_TYPES = [
  { value: 'bug', label: 'Bug' },
  { value: 'suggestion', label: 'Sugerencia' },
  { value: 'question', label: 'Pregunta' },
];

const URGENCY_LEVELS = [
  { value: 'high', label: 'Alta' },
  { value: 'medium', label: 'Media' },
  { value: 'low', label: 'Baja' },
];

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_TEXT_LENGTH = 1000;
const MAX_SCREENSHOTS = 10;

// ─── Section detection ───────────────────────────────────────────────────────

function detectSection(pathname) {
  if (pathname === '/' || pathname === '/inicio' || pathname === '/dashboard') {
    return { section: 'Dashboard', subsection: null };
  }
  if (pathname.startsWith('/causas/') && pathname.split('/').length > 3) {
    const parts = pathname.split('/');
    if (parts[3] === 'cotizar')
      return { section: 'Causas', subsection: 'Cotizar' };
    return { section: 'Causas', subsection: 'Detalle' };
  }
  if (pathname.startsWith('/causas'))
    return { section: 'Causas', subsection: 'Lista' };
  if (pathname === '/diligencias/nueva')
    return { section: 'Diligencias', subsection: 'Nueva' };
  if (pathname.startsWith('/diligencias'))
    return { section: 'Diligencias', subsection: 'Lista' };
  if (pathname.startsWith('/notificaciones/'))
    return { section: 'Notificaciones', subsection: 'Detalle' };
  if (pathname.startsWith('/notificaciones'))
    return { section: 'Notificaciones', subsection: 'Lista' };
  if (pathname.startsWith('/cotizaciones/'))
    return { section: 'Cotizaciones', subsection: 'Detalle' };
  if (pathname.startsWith('/cotizaciones'))
    return { section: 'Cotizaciones', subsection: 'Lista' };
  if (pathname.startsWith('/facturacion'))
    return { section: 'Facturación', subsection: null };
  if (pathname.startsWith('/sistema/organizacion'))
    return { section: 'Sistema', subsection: 'Organización' };
  if (pathname.startsWith('/sistema/cuenta'))
    return { section: 'Sistema', subsection: 'Cuenta' };
  if (pathname.startsWith('/sistema') || pathname.startsWith('/configuracion'))
    return { section: 'Sistema', subsection: 'General' };
  if (pathname.startsWith('/ayuda'))
    return { section: 'Ayuda', subsection: null };
  return { section: null, subsection: null };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target.result;
      resolve(result.split(',')[1]);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Read failed'));
    reader.readAsDataURL(file);
  });
}

function makeId() {
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function captureContext(section, subsection) {
  return {
    pageUrl: window.location.href,
    ...(section != null && { section }),
    ...(subsection != null && { subsection }),
    userAgent: navigator.userAgent,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    clientTimestamp: new Date().toISOString(),
  };
}

function validate(form) {
  const errors = {};
  if (!form.whatDoing.trim()) errors.whatDoing = 'Campo requerido';
  if (!form.whatHappened.trim()) errors.whatHappened = 'Campo requerido';
  return errors;
}

const defaultForm = {
  type: 'bug',
  urgency: 'medium',
  whatDoing: '',
  whatExpected: '',
  whatHappened: '',
  screenshots: [],
};

// ─── Styles (spacing unit = 8px) ───────────────────────────────────────────────

const labelTextStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  cursor: 'pointer',
  fontSize: '0.85rem',
};

const fieldLegendStyle = {
  fontSize: '0.78rem',
  marginBottom: 4,
  color: 'var(--ink-2)',
};

const fieldLabelStyle = {
  fontSize: '0.78rem',
  marginBottom: 4,
  display: 'block',
  color: 'var(--ink-2)',
};

const errorTextStyle = {
  fontSize: '0.75rem',
  color: 'var(--err)',
  display: 'block',
  marginTop: 4,
};

const hintStyle = { fontSize: '0.75rem', color: 'var(--ink-3)' };

// ─── Component ─────────────────────────────────────────────────────────────────

export default function FeedbackForm({
  variant = 'panel',
  onClose,
  onDraftChange,
}) {
  const location = useLocation();
  const { section, subsection } = detectSection(location.pathname);

  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(null);
  const fileInputRef = useRef(null);
  const screenshotsRef = useRef(form.screenshots);

  const mutation = useMutation({ mutationFn: submitFeedback });
  const isPending = mutation.isPending;

  useEffect(() => {
    screenshotsRef.current = form.screenshots;
  }, [form.screenshots]);

  // Report a short draft preview so the FAB minimized chip can label itself.
  useEffect(() => {
    if (!onDraftChange) return;
    const text = form.whatHappened || form.whatDoing;
    onDraftChange(text ? text.slice(0, 40) : null);
  }, [form.whatHappened, form.whatDoing, onDraftChange]);

  // Revoke object URLs on unmount — use ref to always access the latest list.
  useEffect(() => {
    return () => {
      screenshotsRef.current.forEach((s) => URL.revokeObjectURL(s.previewUrl));
    };
  }, []);

  const addScreenshotFromFile = useCallback(async (file) => {
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      return 'Solo se admiten archivos .png o .jpg';
    }
    if (file.size > MAX_FILE_BYTES) {
      return 'La imagen supera el límite de 5 MB';
    }
    try {
      const base64 = await fileToBase64(file);
      setForm((f) => {
        if (f.screenshots.length >= MAX_SCREENSHOTS) return f;
        const previewUrl = URL.createObjectURL(file);
        return {
          ...f,
          screenshots: [
            ...f.screenshots,
            {
              id: makeId(),
              base64,
              previewUrl,
              name: file.name || 'pegada.png',
            },
          ],
        };
      });
      return null;
    } catch {
      return 'No se pudo leer la imagen';
    }
  }, []);

  const handlePaste = useCallback(
    (e) => {
      const items = Array.from(e.clipboardData?.items ?? []);
      const files = items
        .filter(
          (item) => item.kind === 'file' && item.type.startsWith('image/'),
        )
        .map((item) => item.getAsFile())
        .filter(Boolean);
      if (files.length === 0) return;
      e.preventDefault();
      (async () => {
        for (const file of files) {
          const err = await addScreenshotFromFile(file);
          if (err) {
            setErrors((prev) => ({ ...prev, screenshot: err }));
            return;
          }
        }
        setErrors((prev) => ({ ...prev, screenshot: undefined }));
      })();
    },
    [addScreenshotFromFile],
  );

  function handleFileInput(e) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    (async () => {
      for (const file of files) {
        const err = await addScreenshotFromFile(file);
        if (err) {
          setErrors((prev) => ({ ...prev, screenshot: err }));
          return;
        }
      }
      setErrors((prev) => ({ ...prev, screenshot: undefined }));
    })();
  }

  function removeScreenshot(id) {
    setForm((f) => {
      const target = f.screenshots.find((s) => s.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return { ...f, screenshots: f.screenshots.filter((s) => s.id !== id) };
    });
  }

  function handleSubmit() {
    const v = validate(form);
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    setSubmitError(null);
    const ctx = captureContext(section, subsection);

    mutation.mutate(
      {
        type: form.type,
        urgency: form.urgency,
        whatDoing: form.whatDoing.trim(),
        whatExpected: form.whatExpected.trim() || undefined,
        whatHappened: form.whatHappened.trim(),
        ...(form.screenshots.length > 0
          ? { screenshotsBase64: form.screenshots.map((s) => s.base64) }
          : {}),
        context: ctx,
      },
      {
        onSuccess: (response) => {
          // recepthor-api wraps results in { data, success, timestamp }, so
          // issueIdentifier/issueUrl live under `.data`.
          const result = response?.data;
          if (!response?.success || !result?.issueIdentifier) {
            setSubmitError(
              'No se pudo registrar el reporte. Inténtalo de nuevo.',
            );
            return;
          }
          setSuccess(result);
        },
        onError: () => {
          setSubmitError(
            'No se pudo enviar. Inténtalo de nuevo o escríbenos por correo.',
          );
        },
      },
    );
  }

  function handleStartNew() {
    setForm((current) => {
      current.screenshots.forEach((s) => URL.revokeObjectURL(s.previewUrl));
      return defaultForm;
    });
    setErrors({});
    setSubmitError(null);
    setSuccess(null);
    mutation.reset();
  }

  const sectionLabel = section
    ? subsection
      ? `${section} > ${subsection}`
      : section
    : null;

  const rootStyle =
    variant === 'panel'
      ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }
      : { display: 'flex', flexDirection: 'column' };

  // ── Success state ───────────────────────────────────────────────────────────
  if (success) {
    return (
      <div style={rootStyle}>
        <div
          style={{
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <Alert tone="success" role="status">
            ¡Reporte enviado! Ticket:{' '}
            {success.issueUrl ? (
              <a
                href={success.issueUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontWeight: 600 }}
              >
                {success.issueIdentifier}
              </a>
            ) : (
              <span style={{ fontWeight: 600 }}>{success.issueIdentifier}</span>
            )}
          </Alert>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onClose}
            >
              Cerrar
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleStartNew}
            >
              Nuevo reporte
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  return (
    <div style={rootStyle} onPaste={handlePaste}>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          {sectionLabel && (
            <span style={{ fontSize: '0.75rem', color: 'var(--ink-3)' }}>
              Sección: {sectionLabel}
            </span>
          )}

          {submitError && (
            <Alert tone="error" role="alert">
              {submitError}
            </Alert>
          )}

          {/* Tipo */}
          <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
            <legend style={fieldLegendStyle}>Tipo</legend>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {FEEDBACK_TYPES.map(({ value, label }) => (
                <label key={value} style={labelTextStyle}>
                  <Radio
                    checked={form.type === value}
                    onChange={() => setForm((f) => ({ ...f, type: value }))}
                    aria-label={label}
                  />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>

          {/* Urgencia */}
          <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
            <legend style={fieldLegendStyle}>Urgencia</legend>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {URGENCY_LEVELS.map(({ value, label }) => (
                <label key={value} style={labelTextStyle}>
                  <Radio
                    checked={form.urgency === value}
                    onChange={() => setForm((f) => ({ ...f, urgency: value }))}
                    aria-label={label}
                  />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>

          {/* ¿Qué estaba haciendo? */}
          <div>
            <label style={fieldLabelStyle} htmlFor="feedback-what-doing">
              ¿Qué estaba haciendo? *
            </label>
            <Textarea
              id="feedback-what-doing"
              rows={2}
              value={form.whatDoing}
              error={!!errors.whatDoing}
              onChange={(e) => {
                if (e.target.value.length <= MAX_TEXT_LENGTH) {
                  setForm((f) => ({ ...f, whatDoing: e.target.value }));
                  setErrors((err) => ({ ...err, whatDoing: undefined }));
                }
              }}
            />
            {errors.whatDoing && (
              <span style={errorTextStyle}>{errors.whatDoing}</span>
            )}
          </div>

          {/* ¿Qué esperaba que pasara? */}
          <div>
            <label style={fieldLabelStyle} htmlFor="feedback-what-expected">
              ¿Qué esperaba que pasara?
            </label>
            <Textarea
              id="feedback-what-expected"
              rows={2}
              value={form.whatExpected}
              onChange={(e) => {
                if (e.target.value.length <= MAX_TEXT_LENGTH) {
                  setForm((f) => ({ ...f, whatExpected: e.target.value }));
                }
              }}
            />
          </div>

          {/* ¿Qué pasó? */}
          <div>
            <label style={fieldLabelStyle} htmlFor="feedback-what-happened">
              ¿Qué pasó? *
            </label>
            <Textarea
              id="feedback-what-happened"
              rows={3}
              value={form.whatHappened}
              error={!!errors.whatHappened}
              onChange={(e) => {
                if (e.target.value.length <= MAX_TEXT_LENGTH) {
                  setForm((f) => ({ ...f, whatHappened: e.target.value }));
                  setErrors((err) => ({ ...err, whatHappened: undefined }));
                }
              }}
            />
            {errors.whatHappened && (
              <span style={errorTextStyle}>{errors.whatHappened}</span>
            )}
          </div>

          {/* Screenshots */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span style={hintStyle}>
                Capturas{' '}
                <span style={hintStyle}>
                  (Cmd/Ctrl + V para pegar, opcional)
                </span>
              </span>
              {form.screenshots.length > 0 && (
                <span style={hintStyle}>
                  {form.screenshots.length}/{MAX_SCREENSHOTS}
                </span>
              )}
            </div>

            {form.screenshots.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 8,
                }}
              >
                {form.screenshots.map((s) => (
                  <div
                    key={s.id}
                    style={{
                      position: 'relative',
                      aspectRatio: '1',
                      borderRadius: 0,
                      overflow: 'hidden',
                      border: '1px solid var(--rule)',
                      backgroundColor: 'var(--paper)',
                    }}
                  >
                    <img
                      src={s.previewUrl}
                      alt={s.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removeScreenshot(s.id)}
                      aria-label={`Quitar ${s.name}`}
                      style={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 2,
                        borderRadius: '50%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <DeleteIcon size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                Adjuntar archivo
              </Button>
              <span style={hintStyle}>o pegue con Cmd/Ctrl + V aquí</span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                multiple
                onChange={handleFileInput}
                style={{ display: 'none' }}
                aria-label="Adjuntar capturas"
              />
            </div>

            {errors.screenshot && (
              <span style={{ fontSize: '0.75rem', color: 'var(--err)' }}>
                {errors.screenshot}
              </span>
            )}
          </div>
        </form>
      </div>

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: variant === 'modal' ? 'flex-end' : 'space-between',
          gap: 8,
          padding: '10px 16px',
          borderTop: '1px solid var(--rule)',
          flexShrink: 0,
        }}
      >
        {variant === 'panel' && (
          <span style={hintStyle}>
            Minimiza para tomar capturas sin perder lo que llevas escrito.
          </span>
        )}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {variant === 'modal' && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onClose}
              disabled={isPending}
            >
              Cancelar
            </Button>
          )}
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={isPending}
          >
            {isPending ? 'Enviando…' : 'Enviar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

FeedbackForm.propTypes = {
  variant: PropTypes.oneOf(['panel', 'modal']),
  onClose: PropTypes.func,
  onDraftChange: PropTypes.func,
};
